import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Button from '../components/Button';
import { useI18n } from '../contexts/I18nContext';
import { useAuth } from '../contexts/AuthContext';

import LogoImage from '../assets/1.png';

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { t, language, setLanguage } = useI18n();
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();

  const toggleLanguage = () => {
    setLanguage(language === 'en' ? 'ar' : 'en');
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
    setMobileMenuOpen(false);
  };

  const navItems = ['about', 'community', 'contact'] as const;

  return (
    <header
      dir="ltr"
      className="fixed top-0 left-0 right-0 z-50 flex justify-center px-4 pt-4 md:px-8"
    >
      <div
        className={`
          relative w-full max-w-6xl overflow-hidden transition-colors duration-150
          ${mobileMenuOpen
            ? 'rounded-3xl bg-white/95 shadow-[0_20px_60px_-15px_rgba(40,32,98,0.3)] border border-violet-200/50'
            : 'rounded-full bg-white/80 shadow-[0_8px_30px_-15px_rgba(55,39,124,0.25)] backdrop-blur-xl border border-white/80 ring-1 ring-black/[0.03]'
          }
          py-2 md:py-2.5
        `}
      >
        <div className="pointer-events-none absolute inset-x-8 top-0 h-[1px] bg-gradient-to-r from-transparent via-violet-500/20 to-transparent" />
        <div className="pointer-events-none absolute -left-14 top-1/2 h-36 w-36 -translate-y-1/2 rounded-full bg-fuchsia-300/15 blur-2xl" />
        <div className="pointer-events-none absolute -right-14 top-1/2 h-36 w-36 -translate-y-1/2 rounded-full bg-indigo-300/15 blur-2xl" />

        <div className="relative px-4 md:px-6">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link
              to="/"
              className="z-10 flex flex-shrink-0 items-center"
              onClick={() => setMobileMenuOpen(false)}
            >
              <img
                src={LogoImage}
                alt="EduNasser Logo"
                className="h-[40px] w-auto sm:h-[48px] object-contain drop-shadow-[0_3px_6px_rgba(83,54,186,0.2)]"
              />
            </Link>

            {/* Desktop Navigation */}
            {!isAuthenticated && (
              <nav className="hidden items-center gap-1 rounded-full border border-violet-100/50 bg-black/[0.01] px-2 py-1 lg:flex">
                {navItems.map((item) => (
                  <Link
                    key={item}
                    to={`/#${item}`}
                    className="group relative rounded-full px-4 py-1.5 text-sm font-medium text-[#444] transition-colors duration-150 hover:text-[#4C32B3]"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <span className="absolute inset-0 -z-10 rounded-full bg-white opacity-0 transition-opacity duration-150 group-hover:opacity-100 shadow-sm" />
                    {t.header.nav[item as keyof typeof t.header.nav]}
                  </Link>
                ))}
              </nav>
            )}

            {/* Right side actions */}
            <div className="z-10 flex items-center gap-2 sm:gap-3">
              {/* Language Switcher */}
              <button
                onClick={toggleLanguage}
                className="inline-flex h-8 items-center justify-center rounded-full border border-violet-200/50 bg-white px-3 text-xs font-semibold text-[#555] transition-colors duration-150 hover:border-violet-300 hover:text-[#4D31B7] sm:h-9 sm:text-sm"
                aria-label="Switch language"
              >
                {language === 'en' ? 'العربية' : 'English'}
              </button>

              {isAuthenticated ? (
                <>
                  <Link
                    to="/dashboard"
                    className="hidden rounded-full px-3 py-1.5 text-sm font-medium text-[#555] transition-colors duration-150 hover:text-[#4D31B7] md:inline"
                  >
                    {t.dashboard.title}
                  </Link>
                  <div className="hidden items-center gap-3 border-l border-violet-200/60 pl-3 md:flex">
                    <span className="text-sm font-medium text-[#2C2C2C]">
                      {user?.name}
                    </span>
                    <Button
                      variant="secondary"
                      className="h-[34px] min-h-0 rounded-full border-violet-300/35 bg-white/80 px-4 py-0 text-xs hover:bg-violet-50"
                      onClick={handleLogout}
                    >
                      {t.common.logout}
                    </Button>
                  </div>
                </>
              ) : (
                <div className="hidden items-center gap-2 md:flex">
                  <Link
                    to="/login"
                    className="rounded-full px-3 py-2 text-sm font-medium text-[#555] transition-colors duration-150 hover:bg-violet-50/80 hover:text-[#4D31B7]"
                  >
                    {t.header.login}
                  </Link>
                  <Link to="/register">
                    <Button
                      variant="primary"
                      className="h-[36px] min-h-0 rounded-full border border-violet-300/20 bg-gradient-to-r from-[#5B3FD0] via-[#6B49E2] to-[#3D8BFF] px-5 py-0 text-xs shadow-[0_6px_20px_-10px_rgba(88,56,205,0.85)]"
                    >
                      {t.header.signup}
                    </Button>
                  </Link>
                </div>
              )}

              {/* Mobile menu button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="rounded-full border border-violet-200/60 bg-white/70 p-2.5 text-[#444] transition-colors duration-150 hover:bg-white hover:text-[#5B3FD0] lg:hidden"
                aria-label="Toggle menu"
              >
                <div className="w-5 h-[16px] relative flex flex-col justify-between">
                  <span className={`w-full h-[2px] bg-current rounded-full transition-transform duration-150 ${mobileMenuOpen ? 'rotate-45 translate-y-[7px]' : ''}`} />
                  <span className={`w-full h-[2px] bg-current rounded-full transition-opacity duration-150 ${mobileMenuOpen ? 'opacity-0' : ''}`} />
                  <span className={`w-full h-[2px] bg-current rounded-full transition-transform duration-150 ${mobileMenuOpen ? '-rotate-45 -translate-y-[7px]' : ''}`} />
                </div>
              </button>
            </div>
          </div>

          {/* Mobile menu */}
          <div
            className={`overflow-hidden lg:hidden transition-opacity duration-150 ${
              mobileMenuOpen
                ? 'mt-4 max-h-[480px] opacity-100 pb-4'
                : 'max-h-0 opacity-0 pointer-events-none'
            }`}
          >
            <div className="rounded-2xl border border-violet-200/55 bg-gradient-to-b from-white/95 via-violet-50/55 to-indigo-50/45 px-2 py-2 shadow-[0_16px_40px_-30px_rgba(70,52,175,0.7)]">
              {!isAuthenticated && (
                <nav className="flex flex-col gap-1">
                  {navItems.map((item) => (
                    <Link
                      key={item}
                      to={`/#${item}`}
                      className="rounded-xl px-4 py-2.5 font-medium text-[#2C2C2C]/75 transition-all duration-200 active:bg-violet-100 active:text-[#5B3FD0]"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {t.header.nav[item as keyof typeof t.header.nav]}
                    </Link>
                  ))}
                </nav>
              )}

              <div className="mt-3 flex flex-col gap-3 border-t border-violet-200/70 pt-3">
                {isAuthenticated ? (
                  <>
                    <Link
                      to="/dashboard"
                      className="rounded-xl px-4 py-2 text-sm font-medium text-[#555] active:bg-violet-100 active:text-[#5B3FD0]"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {t.dashboard.title}
                    </Link>
                    <div className="flex items-center justify-between rounded-xl border border-violet-200/60 bg-white/75 px-4 py-2.5">
                      <span className="text-sm font-medium text-[#2C2C2C]">{user?.name}</span>
                      <button
                        className="text-sm font-medium text-red-500 transition-colors duration-200 hover:text-red-600"
                        onClick={handleLogout}
                      >
                        {t.common.logout}
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="flex flex-col gap-2 px-4">
                    <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="secondary" className="w-full justify-center rounded-full bg-white/75">
                        {t.header.login}
                      </Button>
                    </Link>
                    <Link to="/register" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="primary" className="w-full justify-center rounded-full bg-gradient-to-r from-[#5B3FD0] via-[#6B49E2] to-[#3D8BFF]">
                        {t.header.signup}
                      </Button>
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
